import { createSlice } from "@reduxjs/toolkit";
import {
  USER_DATA_LOAD_STATUS,
  USER_TOKEN_STATUS,
} from "../../constants/metaData";
import {
  ON_USER_DATA_LOAD,
  ON_IFS_USER_LOAD,
  ON_MESSAGE_BOX_INVOKE,
  ON_TOKEN_FAIL,
} from "../../constants/actionTypes";
import {
  ERROR_TYPE,
  AZURE_USER_AUTHENTICATION_FAILURE,
  FORBIDDEN,
  NO_USER_ACCOUNT,
  MICROSOFT_ACCOUNT_ISSUE_MESSAGE,
} from "../../constants/messageTypes";

const initialState = {
  userDataLoadStatus: USER_DATA_LOAD_STATUS.onStart,
  ifsUserStatus: USER_TOKEN_STATUS.invalid,
  // Used by LoginPage (LoadingView + LoginErrorView)
  messageBoxInfo: {},
};

const authFlowSlice = createSlice({
  name: "AuthFlow",
  initialState,
  reducers: {
    clearAuthFlowMessageBox(state) {
      state.messageBoxInfo = {};
    },
    // Used by login thunks to show auth errors on LoginPage (ignore global legacy MessageBox).
    setAuthMessageBoxInfo(state, { payload }) {
      state.messageBoxInfo = payload ?? {};
    },
  },
  extraReducers: (builder) => {
    // Keep compatibility with existing login thunks dispatching legacy action types.
    builder.addCase(ON_USER_DATA_LOAD, (state, action) => {
      state.userDataLoadStatus = action.payload;
    });
    builder.addCase(ON_IFS_USER_LOAD, (state, action) => {
      state.ifsUserStatus = action.payload;
    });
    builder.addCase(ON_MESSAGE_BOX_INVOKE, (state, action) => {
      state.messageBoxInfo = action.payload ?? {};
    });

    // login thunks use ON_TOKEN_FAIL for Azure token errors; legacy reducer mapped it.
    builder.addCase(ON_TOKEN_FAIL, (state, action) => {
      const payload = action.payload;

      // Default unauthorized state (defensive)
      state.userDataLoadStatus = USER_DATA_LOAD_STATUS.onAzureUnauthorizedUser;
      state.messageBoxInfo = {
        type: ERROR_TYPE,
        message: MICROSOFT_ACCOUNT_ISSUE_MESSAGE,
        errorMessage: AZURE_USER_AUTHENTICATION_FAILURE,
        error: AZURE_USER_AUTHENTICATION_FAILURE,
      };

      if (!payload) return;

      const responseCode = payload.responseCode;
      const errorCodeMessage = payload.errorMessage
        ? payload.errorMessage
        : AZURE_USER_AUTHENTICATION_FAILURE;

      state.messageBoxInfo = {
        type: ERROR_TYPE,
        message:
          responseCode === 403
            ? FORBIDDEN
            : responseCode === 204
              ? NO_USER_ACCOUNT
              : MICROSOFT_ACCOUNT_ISSUE_MESSAGE,
        errorMessage: `Error Code: ${responseCode}: ${errorCodeMessage}`,
        error: payload.error ?? errorCodeMessage,
      };

      if (
        responseCode === 400 ||
        responseCode === 401 ||
        responseCode === 403 ||
        responseCode === 404 ||
        responseCode === 204
      ) {
        state.userDataLoadStatus = USER_DATA_LOAD_STATUS.onAzureUnauthorizedUser;
      } else {
        state.userDataLoadStatus = USER_DATA_LOAD_STATUS.onAzureUnauthorizedUser;
      }
    });
  },
});

export const { clearAuthFlowMessageBox, setAuthMessageBoxInfo } = authFlowSlice.actions;
export default authFlowSlice.reducer;

